import { useCallback, useContext } from "react";
import { useHistory, useRouteMatch } from "react-router";

import { mobile } from "@btnet/utilities-webview";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";

import type { ISearchHit } from "commonComponents/utilities/MainNavigation/SearchDialog/search/search.types";

import { getEntityLink, navigateEntityLink } from "./entityLink";

export interface IEntityNavigation {
    /**
     * Whether this hit resolves to a route at all. Some entities need a field the
     * search payload does not always carry (a job-scoped hit with no `jobId`, a
     * submittal with no `externalId`), and some entity types have no route.
     */
    isRoutable: (hit: ISearchHit) => boolean;
    /** Navigate to the hit. A no-op when it is not routable. */
    open: (hit: ISearchHit) => void;
}

/**
 * Opening a record from search — the one implementation of it, so result rows,
 * recents and citation chips cannot route the same record differently.
 */
export function useEntityNavigation(
    builderInfo: BuilderInfo | null | undefined,
): IEntityNavigation {
    const history = useHistory();
    const match = useRouteMatch();
    const spaContext = useContext(SpaInfoContext);

    const isRoutable = useCallback(
        (hit: ISearchHit) => getEntityLink(hit, builderInfo ?? undefined) !== null,
        [builderInfo],
    );

    const open = useCallback(
        (hit: ISearchHit) => {
            if (openInMobileApp(hit)) {
                return;
            }
            const link = getEntityLink(hit, builderInfo ?? undefined);
            if (!link) {
                return;
            }
            navigateEntityLink(link, history, match.url, !!spaContext.isSpa);
        },
        [builderInfo, history, match.url, spaContext.isSpa],
    );

    return { isRoutable, open };
}

let lastHandedToAppAt = 0;

// Not in the published @bt-mobile/shared-contracts yet; drop once it is.
interface IOpenSearchResult {
    onOpenSearchResult?: (payload: {
        entity: string;
        id: string;
        payload?: Record<string, unknown>;
    }) => void;
}

/**
 * Inside the mobile app, hands the hit to the app so it opens its own screen for
 * the record. Returns false when the app doesn't register the handler (older
 * versions), so the caller falls back to the web link.
 */
function openInMobileApp(hit: ISearchHit): boolean {
    if (!mobile.isWebview) {
        return false;
    }
    const { onOpenSearchResult } = mobile as unknown as IOpenSearchResult;
    if (typeof onOpenSearchResult !== "function") {
        return false;
    }
    onOpenSearchResult({ entity: hit.entity, id: hit.id, payload: hit.payload });
    lastHandedToAppAt = Date.now();
    return true;
}

/**
 * True right after a hit was handed to the mobile app. The dialog closes itself
 * once a record opens; the search webview page uses this to keep that close from
 * also closing the app screen the record is opening on top of.
 */
export function wasJustHandedToMobileApp(): boolean {
    return Date.now() - lastHandedToAppAt < 1000;
}
